import { Col, Flex, View } from "@amzn/stencil-react-components/layout";
import { ProgressBar } from "@amzn/stencil-react-components/progress-bar";
import { IconDocument, IconBin, IconCross, IconRefresh, IconUpload } from "@amzn/stencil-react-components/icons";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import React from "react";
import { HVH_COLORS } from "../../../config/palette";
import {
  DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE,
  DOC_UPLOAD_ITEM_WIDGET_VALID_STATE,
  EDM_REVIEW_STATUS,
} from "../../../utility/enums/common";
import useArb from "../../../hooks/use-arb";
import { Text } from "@amzn/stencil-react-components/text";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { DocumentWidget } from "../../../utility/types/common";
import { ModalContent, WithModal } from "@amzn/stencil-react-components/modal";
import { useAppDispatch } from "../../../reduxStore/reduxHooks";
import { removeFromDocsWidget } from "../../../reduxStore/slices/docUploadSlice";
import { isApprovedByEDM } from "../../../helpers/utils";

interface DocumentWidgetItemProps {
  doc: DocumentWidget;
  refetch: Function;
}

const DocumentWidgetItem = ({ doc, refetch }: DocumentWidgetItemProps) => {
  const { fileName, contentType, contentSize, state, documentId, reviewStatus } = doc;
  const dispatch = useAppDispatch();

  const { getMessage } = useArb("HVHChecklistPantherResource");

  const handleCloseWidget = () => {
    dispatch(removeFromDocsWidget({ documentId }));
    refetch();
  };

  const handleDiscardWidget = (callback: Function) => {
    dispatch(removeFromDocsWidget({ documentId }));
    refetch();
    callback();
  };

  const handleReuploadWidget = () => {
    // call query to reupload document to EDM
    refetch();
  };

  const handleUploadNewFileWidget = () => {
    // TODO: connect to upload new file page, keep the ID as reference to update docWidgets
  };

  const isInFailedState = state in DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE;

  const renderWidgetDetail = () => {
    let docDetail = <></>;
    let actionButtons = <></>;

    // Setting up the documents details
    if (state === DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.PROCESSING) {
      docDetail = (
        <>
          <IconDocument title={fileName} color={HVH_COLORS.PRIMARY_70} margin={{ top: 5, right: 5 }} />
          <Text fontSize={"T300"} color={HVH_COLORS.NEUTRAL_70}>
            {getMessage("Checklist-docUpload-widget-uploading", "Uploading...")}
          </Text>
        </>
      );
    } else {
      docDetail = (
        <>
          <IconDocument title={fileName} color={HVH_COLORS.PRIMARY_70} margin={{ top: 5, right: 5 }} />
          <Flex flexDirection="column">
            <Text fontSize={"T300"} color={HVH_COLORS.NEUTRAL_90}>
              {fileName}
            </Text>
            <Text fontSize={"T100"} color={HVH_COLORS.NEUTRAL_70}>
              {contentType.toUpperCase()} {contentSize}
            </Text>
          </Flex>
        </>
      );
    }

    // Setting up the action buttons
    if (state === DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.PROCESSING) {
      actionButtons = (
        <Button
          css={{ marginLeft: 5 }}
          icon={<IconCross title="Close" />}
          onClick={handleCloseWidget}
          variant={ButtonVariant.Tertiary}
        />
      );
    } else if (state === DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.SUCCESSFUL) {
      actionButtons = (
        <WithModal renderModal={renderModal} isScrollable={false} shouldCloseOnClickOutside={false}>
          {({ open }) => (
            <>
              <Button
                aria-describedby="sroDialog"
                data-testid="deleteDocWidget"
                onClick={open}
                icon={<IconBin color={HVH_COLORS.RED_70} title="Discard" />}
                variant={ButtonVariant.Tertiary}
              />
              <span hidden id="sroDialog">
                has pop-up dialog
              </span>
            </>
          )}
        </WithModal>
      );
    } else if (state === DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.FAILED_UPLOAD) {
      actionButtons = (
        <>
          <Button
            icon={<IconCross color={HVH_COLORS.PRIMARY_70} title="Close" />}
            onClick={handleCloseWidget}
            variant={ButtonVariant.Tertiary}
          />
          {!isApprovedByEDM(reviewStatus) && (
            <Button
              icon={<IconRefresh color={HVH_COLORS.PRIMARY_70} title="Reupload file" />}
              onClick={handleReuploadWidget}
              variant={ButtonVariant.Tertiary}
            />
          )}
        </>
      );
    } else if (state in DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE) {
      actionButtons = (
        <>
          <Button
            icon={<IconUpload color={HVH_COLORS.PRIMARY_70} title="Upload new file" />}
            onClick={handleUploadNewFileWidget}
            variant={ButtonVariant.Tertiary}
          />
          <Button
            icon={<IconCross color={HVH_COLORS.PRIMARY_70} title="Close" />}
            onClick={handleCloseWidget}
            variant={ButtonVariant.Tertiary}
          />
        </>
      );
    }

    return (
      <Flex flexDirection="row" alignItems="center" justifyContent="space-between">
        <Flex flexDirection="row">{docDetail}</Flex>
        <Flex flexDirection="row">{actionButtons}</Flex>
      </Flex>
    );
  };

  const renderErrorMessage = () => {
    if (isInFailedState) {
      let errorMessage = "";
      if (state === DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.FAILED_UPLOAD) {
        errorMessage = getMessage("Checklist-docUpload-widget-error-failed-upload", "Failed upload, please try again");
      }
      if (state === DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.NOT_SUPPORTED_FORMAT) {
        errorMessage = getMessage(
          "Checklist-docUpload-widget-error-not-supported-format",
          "Image does not meet the accepted format: JPEG, JPG, PDF, PNG"
        );
      }
      if (state === DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.SIZE_EXCEEDED) {
        errorMessage = getMessage(
          "Checklist-docUpload-widget-error-size-exceeded",
          "Image exceeds the maximum size of 7MB"
        );
      }

      return <StatusIndicator messageText={errorMessage} status={Status.Negative} />;
    }
  };

  const renderModal = ({ close }: { close: () => void }) => (
    <ModalContent
      data-modal-type="upload-doc-delete"
      titleText={getMessage("Checklist-docUpload-widget-modal-header", "Delete document?")}
      buttons={[
        <Button
          key={`Checklist-docUpload-modal-cancel-${documentId}`}
          onClick={close}
          variant={ButtonVariant.Secondary}
        >
          <Text fontWeight={"regular"}>{getMessage("Checklist-docUpload-widget-modal-cancel", "No, cancel")}</Text>
        </Button>,
        <Button
          key={`Checklist-docUpload-modal-delete-${documentId}`}
          onClick={() => handleDiscardWidget(close)}
          variant={ButtonVariant.Primary}
          isDestructive
        >
          <Text fontWeight={"regular"}>{getMessage("Checklist-docUpload-widget-modal-continue", "Yes, delete")}</Text>
        </Button>,
      ]}
    >
      <Text>
        {getMessage(
          "Checklist-docUpload-widget-modal-description",
          "Are you sure you would like to delete this document and all of its attachments?"
        )}
      </Text>
    </ModalContent>
  );

  return (
    <Col gridGap="S100">
      <Col padding="S300" gridGap="S300" backgroundColor={isInFailedState ? HVH_COLORS.RED_10 : HVH_COLORS.NEUTRAL_05}>
        {renderWidgetDetail()}
      </Col>
      {renderErrorMessage()}
    </Col>
  );
};

export default DocumentWidgetItem;
